import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { BrowserRouter } from 'react-router-dom';
import { toast, Toaster } from '@yakit/ui-components';
import remarkGfm from 'remark-gfm';
import * as Portal from '@radix-ui/react-portal';
import cookie from 'js-cookie';
import Chat from 'chat_assistant/ChatAssistant';

import { VLDIframeModal } from '@VDL/components/VDLModals/VDLIframeModal';
import { VDLPassportModal } from '@VDL/components/VDLModals/VDLPassportModal';

import { AppRouter } from '@VDL/router';
import { useVDLStore } from '@VDL/store/VDLstore';

import { useFetchYearsData } from '@VDL/hooks/useFetchYearsData';
import { useFetchLevelData } from '@VDL/hooks/useFetchLevelData';

import { HubConnectionBuilder } from '@microsoft/signalr';
import { parseJWT } from './utils/parseJWT';

interface IWSNotify {
  message: string | null;
  link: string | null;
  status?: string;
}

const MARKDOWN = `
## 🤖 Уведомление от AI-агента

**Статус:** Выполнено успешно  
**Время:** 05.03.2026 · 14:32  

### Что произошло
Я завершил обработку вашего последнего запроса и обновил связанные данные.

### Детали
- Проанализировано: **3 источника данных**
- Обновлено: **2 записи**
- Потенциальных ошибок: **не обнаружено**

### Рекомендация
Вы можете проверить обновлённую информацию или продолжить работу.  
Если нужно — я могу выполнить дополнительные действия.

---

💡 *Подсказка:* напишите команду или выберите действие в интерфейсе, чтобы продолжить.
`.trim();

const VDLContent = () => {
  const { selectedYear } = useVDLStore();

  useFetchYearsData();
  const { fetchData } = useFetchLevelData({ year: selectedYear });

  const [isShowChat, setIsShowChat] = useState(false);
  useEffect(() => {
    (async () => {
      const r = await fetch(
        import.meta.env.DEV
          ? 'https://vdl.yakit.pro/apiassistant/main/vdl/access'
          : '/apiassistant/main/vdl/access',
        {
          headers: {
            Authorization: import.meta.env.PROD
              ? cookie.get(import.meta.env.VITE_TOKEN_KEY)
              : import.meta.env.VITE_API_KEY
          }
        }
      );
      const json = (await r.json()) as { access: boolean };

      if (import.meta.env.VITE_INCLUDES_CHAT && json.access) {
        setIsShowChat(true);
      }
    })();
  }, []);

  const showInfo = (
    status: string,
    text: string,
    link?: { url: string | null; label: string }
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (toast as any)[status](
      <div className="prose prose-sm">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>

        {link?.url && (
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {link.label}
          </a>
        )}
      </div>,
      { icon: null, duration: Infinity, closeButton: true }
    );
  };

  useEffect(() => {
    if (import.meta.env.DEV) {
      const timeoutId = setTimeout(() => {
        showInfo('error', MARKDOWN);
      }, 2500);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.VITE_INCLUDES_WS_NOTIFY !== 'true') return;

    let userId;
    let parsed;
    const token = import.meta.env.DEV
      ? import.meta.env.VITE_API_KEY
      : cookie.get(import.meta.env.VITE_TOKEN_KEY);
    try {
      parsed = parseJWT(token);
      userId = parsed.id as number;
    } catch {
      throw new Error('Ошибка после расшифровки токена');
    }

    const url = import.meta.env.DEV
      ? `https://vdl.yakit.pro/v8/api/vdl/hubs/notification/${userId}`
      : `/v8/api/vdl/hubs/notification/${userId}`;

    const connection = new HubConnectionBuilder()
      .withUrl(url)
      .withAutomaticReconnect()
      .build();

    connection.start().then(() => console.log('Подрубился'));

    connection.on('ReceivedMessage', (data) => {
      const obj = JSON.parse(data) as IWSNotify;
      const status = obj?.status;
      showInfo(status ?? 'info', obj.message ?? '', {
        url: obj.link,
        label: 'ссылка'
      });
    });

    connection.onclose((error) => {
      toast.message('Соединение WebSocket закрыто');

      if (error) console.error('Ошибка при закрытии WebSocket');
    });

    return () => {
      connection.stop();
    };
  }, []);

  return (
    <>
      <AppRouter />
      <VLDIframeModal
        onClose={() => {
          if (!selectedYear) return;

          fetchData({ year: selectedYear });
        }}
      />
      <VDLPassportModal />
      {isShowChat && <Chat />}
      <Portal.Root>
        <Toaster
          richColors
          theme="light"
          expand={true}
          visibleToasts={9}
          toastOptions={{
            style: {
              width: '25vw'
            },
            classNames: {
              closeButton: 'toast-close-button'
            }
          }}
        />
      </Portal.Root>
    </>
  );
};

const VDL = () => {
  return (
    <BrowserRouter basename={import.meta.env.VITE_ROUTER_BASENAME}>
      <VDLContent />
    </BrowserRouter>
  );
};

export default VDL;
